import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// eslint-disable-next-line import/no-extraneous-dependencies
import { allowedExtensions, validExtensionTypes } from 'shared/utils';
import { ReactComponent as UploaderIcon } from 'assets/icon-up-file.svg';
import { ReactComponent as TrackRightIcon } from 'assets/icon-track-right.svg';
import FileSize from 'shared/helpers/fileSize';
import { colors } from 'shared/defaultTheme';
import { ButtonIcon } from 'shared/components/Buttons';
import { SpanCustom, Upload, UpdateDocumentFile } from './styles';
import { UploadedImage } from '../SharedStyles/SharedStyles';

const InputFile = ({
  name,
  disabled,
  className,
  onChangeFile,
  validTypes,
  showUploadedImage,
  uploadedImage,
  placeholder,
  onError,
  error,
  updateDocument,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef();
  const [fileName, setFileName] = useState(name !== '' ? name : '');
  const [isDrop, setIsDrop] = useState(false);
  const validateTypeFile = (file) => {
    const existExtension = validTypes.some(
      (x) => file.name.toUpperCase().endsWith(x) || file.name.toLowerCase().endsWith(x)
    );
    if (!existExtension) {
      return false;
    }
    return true;
  };

  const callOnChangeFile = (file) => {
    if (validateTypeFile(file)) {
      setFileName(file.name);
      onChangeFile(file);
      onError('');
      return;
    }
    onError(t('general_invalid_file_type'));
  };

  const onClickUploadSection = () => {
    fileInputRef?.current?.click();
  };

  const dragOver = (e) => {
    setIsDrop(true);
    e.preventDefault();
  };

  const dragEnter = (e) => {
    setIsDrop(true);
    e.preventDefault();
  };

  const dragLeave = (e) => {
    setIsDrop(false);
    e.preventDefault();
  };

  const fileDrop = (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    const size = FileSize(files[0].size);
    const typeSize = size.split(' ')[1];
    setIsDrop(false);
    if (parseInt(size, 10) >= 124 && typeSize === 'mb') {
      onError(t('new_document_text_maximum_size'));
    } else {
      callOnChangeFile(files[0]);
    }
  };

  const fileSelected = () => {
    if (fileInputRef.current.files.length) {
      const { files } = fileInputRef.current;
      const size = FileSize(files[0].size);
      const typeSize = size.split(' ')[1];
      if (parseInt(size, 10) >= 124 && typeSize === 'mb') {
        onError(t('new_document_text_maximum_size'));
      } else {
        callOnChangeFile(files[0]);
      }
    }
  };

  const getPlaceholder = () => {
    return placeholder || t('general_placeholder_input_file');
  };

  return (
    <>
      <div className={classNames(['image-uploader', className])}>
        <Upload
          onClick={onClickUploadSection}
          disabled={disabled}
          aria-hidden="true"
          onDragOver={dragOver}
          onDragEnter={dragEnter}
          isDrop={isDrop}
          onDragLeave={dragLeave}
          onDrop={fileDrop}
          className={classNames(['image-uploader__upload-input-section'], {
            'form__form-group-field--error': error,
            'form__form-group-field': true,
          })}
        >
          {!showUploadedImage && (
            <div className="d-flex justify-content-between pl-2">
              <SpanCustom>
                {fileName !== '' ? fileName : t('general_placeholder_input_file')}
              </SpanCustom>
              {!updateDocument && <UploaderIcon className="upload-section__uploader-icon" />}
            </div>
          )}
          {showUploadedImage && uploadedImage && <UploadedImage imageSrc={uploadedImage} />}
          {showUploadedImage && !uploadedImage && (
            <div className="d-flex justify-content-between pl-2">
              <SpanCustom>{getPlaceholder()}</SpanCustom>
              <UploaderIcon className="upload-section__uploader-icon" />
            </div>
          )}
        </Upload>
        <input
          ref={fileInputRef}
          type="file"
          onChange={fileSelected}
          disabled={disabled}
          accept={allowedExtensions}
        />
      </div>
      {updateDocument && (
        <UpdateDocumentFile className="image-uploader ">
          <ButtonIcon
            id="update-document"
            title={t('document_detail_update')}
            type="button"
            Icon={() => <TrackRightIcon fill={colors.black} className="track-right-icon" />}
            onClick={onClickUploadSection}
            className="button-icon"
          />
          <input
            ref={fileInputRef}
            type="file"
            onChange={fileSelected}
            accept={allowedExtensions}
          />
        </UpdateDocumentFile>
      )}
    </>
  );
};

InputFile.defaultProps = {
  className: '',
  validTypes: validExtensionTypes,
  showUploadedImage: false,
  uploadedImage: '',
  placeholder: '',
  onError: (error) => error,
  error: false,
  name: '',
  disabled: false,
  updateDocument: false,
};

InputFile.propTypes = {
  className: PropTypes.string,
  onChangeFile: PropTypes.func.isRequired,
  validTypes: PropTypes.arrayOf(PropTypes.string),
  showUploadedImage: PropTypes.bool,
  uploadedImage: PropTypes.string,
  placeholder: PropTypes.string,
  onError: PropTypes.func,
  error: PropTypes.bool,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  updateDocument: PropTypes.bool,
};

export default InputFile;
